// @flow
//#region imports
import React, { PureComponent } from "react";
import TogglesHandler from "containers/TogglesHandler";
import "./index.css";
import { connect } from "react-redux";
import { compose } from "redux";
import MainLayout from "components/MainLayout";
import {
    RefreshMode,
    ServerEventItemType,
    ServerEventActionType,
    type TreeItemDTO,
    type ToggleDTO,
} from "data/types";
import TreeFilter from "components/TreeFilter";
import FolderTreeContainer from "containers/FolderTree";
import loc from "i18next";
import Constants, { type ConstantsEnum } from "data/constants";
import DocListContainer from "containers/DocList";
import * as s from "data/reducers/selectors";
import SplitPanes from "containers/SplitPanes";
import GlobalEventsHandler from "containers/GlobalEventsHandler";
import CloseButton from "components/ModalCloseButton";
import CommandActionHandler from "components/CommandActions";
import { LocalStoreHandler } from "containers/LocalStore";
import Pane from "components/SplitPanes/Pane";
//#endregion

export const viewName = "minipicker";

//#region flow types
type Props = {
    /** whether shown as Modal */
    asModal?: boolean,
    /** callback when closing Modal dialog */
    onClose?: () => void,

    // from TogglesHandler
    /** TogglesHandler: current toggles */
    toggles: ?ToggleDTO,

    /** DocList: callback on Document single click
     * @param {SyntheticMouseEvent} e HTML click event
     * @param {string} itemUri the row's itemUri
     * @param {number} index the row's index
     */
    onDocSingleClick?: (
        e: SyntheticMouseEvent,
        itemUri: string,
        index: number
    ) => void,
    /** DocList: callback on Document double click
     * @param {SyntheticMouseEvent} e HTML click event
     * @param {string} itemUri the row's itemUri
     * @param {number} index the row's index
     */
    onDocDoubleClick?: (
        e: SyntheticMouseEvent,
        itemUri: string,
        index: number
    ) => void,

    /** FolderTree callback on Folder single click */
    onFolderSingleClick: (itemUri: string, treeNodeId: number) => void,

    /** optional breadcrumbPart click event instead of history.push */
    onBreadcrumbPartClick?: (itemUri: string) => void,

    // from redux
    /** folders fetch status */
    treeStatus: ConstantsEnum,
    /** folders data rows */
    tree: Array<TreeItemDTO>,
    /** is folder root */
    treeIsRoot: boolean,
    /** is folder browsable */
    treeIsBrowsable: boolean,
    /** total number of results available */
    treeTotalCount: number,
    /** current itemUri */
    itemUri: string,
    /** whether running in Mini view mode */
    isMini: boolean,
    /** whether to limit breadcrumb scope */
    breadcrumbScopeFromLevel?: number,
    /** TogglesHandler: whether multiple toggles are allowed */
    isMultiple: boolean,
    /** TogglesHandler: currently selected toggles' names */
    selectedToggles: Array<string>,
};

type State = {
    /** the real itemUri currently selected */
    activeItemUri: ?string,
    activeDocIndex: number,
    treeNodeId: number,
    /** current tree name filter */
    treeFilter: string,
    includeSubfolders: boolean,
    isTreeFocused: boolean,
};
//#endregion

export class Picker extends PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            activeItemUri: props.itemUri,
            activeDocIndex: -1,
            treeNodeId: null,
            treeFilter: "",
            includeSubfolders: false,
            isTreeFocused: false,
        };
    }

    //#region React
    componentDidUpdate(prevProps: Props) {
        if (prevProps.itemUri !== this.props.itemUri) {
            this.setState((state) => ({
                treeFilter: "",
                activeItemUri: this.props.itemUri,
                isTreeFocused: false,
            }));
        }
    }
    //#endregion

    _onBreadcrumbPartClick = (itemUri: string): void => {
        this.setState({
            treeFilter: "",
            activeItemUri: itemUri,
            treeNodeId: null,
            isTreeFocused: false,
        });
        /* istanbul ignore else */
        if (typeof this.props.onBreadcrumbPartClick === "function")
            this.props.onBreadcrumbPartClick(itemUri);
    };

    //#region Tree

    _onTreeClick = (itemUri: string, treeNodeId: number): void => {
        this.setState({
            activeItemUri: itemUri,
            treeNodeId,
            activeDocIndex: -1,
            isTreeFocused: true,
        });
        /* istanbul ignore else */
        if (typeof this.props.onFolderSingleClick === "function")
            this.props.onFolderSingleClick(itemUri, treeNodeId);
    };

    _onTreeExpand = (itemUri: string): void => {
        /* istanbul ignore else */
        if (typeof this.props.onBreadcrumbPartClick === "function") {
            this.setState({
                treeNodeId: null,
                activeDocIndex: -1,
                isTreeFocused: true,
            });
            this.props.onBreadcrumbPartClick(itemUri);
        }
    };

    _onTreeFilterChange = (value: ?string, includeSubfolders: boolean): void =>
        this.setState({
            treeFilter: value || "",
            includeSubfolders,
            isTreeFocused: true,
        });

    //#endregion

    _showDoc = (itemUri: string, newWindow: boolean) =>
        CommandActionHandler("doc_preview", {
            itemUri,
            newWindow,
        });

    _onIconClick = (
        e: SyntheticMouseEvent<*>,
        itemUri: string,
        rowIndex: number
    ) => {
        e.preventDefault();
        this.setState({
            activeDocIndex: rowIndex,
            isTreeFocused: false,
        });
        const doNewWindow = e.ctrlKey || e.altKey;
        this._showDoc(itemUri, doNewWindow);
    };

    _onDocSingleClick = (
        e: SyntheticMouseEvent<*>,
        itemUri: string,
        rowIndex: number
    ) => {
        this.setState({
            activeDocIndex: rowIndex,
            isTreeFocused: false,
        });
        /* istanbul ignore else */
        if (typeof this.props.onDocSingleClick === "function")
            this.props.onDocSingleClick(e, itemUri, rowIndex);
    };

    render() {
        const { activeItemUri, includeSubfolders } = this.state;
        const treeAvailable =
            this.props.treeIsBrowsable === true &&
            (this.props.treeTotalCount > 0 || this.state.treeFilter.length > 0);
        return (
            <MainLayout
                name={viewName}
                toggles={this.props.toggles}
                itemUri={activeItemUri}
                actionsDisabled={true}
                onBreadcrumbPartClick={this._onBreadcrumbPartClick}
                breadcrumbReadonly={false}
                topRowEnd={<CloseButton onClick={this.props.onClose} />}
                breadcrumbScopeFromLevel={this.props.breadcrumbScopeFromLevel}
            >
                <Pane
                    id="picker_treePanel"
                    className="f-split"
                    style={{
                        backgroundColor:
                            treeAvailable ||
                            this.props.treeStatus === Constants.LOADING
                                ? "#fff"
                                : "#f5f5f5",
                    }}
                >
                    {treeAvailable ? (
                        <TreeFilter
                            key="searchInput"
                            onChange={this._onTreeFilterChange}
                            filter={this.state.treeFilter}
                            includeSubfolders={includeSubfolders}
                            allowSubfolders={true}
                            className="treefilter"
                        />
                    ) : (
                        <div style={{ marginTop: 50 }} />
                    )}
                    <FolderTreeContainer
                        focused={this.state.isTreeFocused}
                        itemUri={this.props.itemUri}
                        activeTreeNodeId={this.state.treeNodeId}
                        filter={this.state.treeFilter}
                        includeSubfolders={includeSubfolders}
                        onClick={this._onTreeClick}
                        onExpand={this._onTreeExpand}
                    />
                </Pane>
                <Pane id="picker_docListPanel" className="f-split">
                    {activeItemUri && (
                        <DocListContainer
                            showAddDocument={false}
                            itemUri={activeItemUri}
                            activeRowIndex={this.state.activeDocIndex}
                            onIconClick={this._onIconClick}
                            onSingleClick={this._onDocSingleClick}
                            onDoubleClick={this.props.onDocDoubleClick}
                        />
                    )}
                </Pane>
            </MainLayout>
        );
    }
}

//#region redux
const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    treeStatus: s.treeStatusSelector(state),
    tree: s.treeNodesSelector(state),
    treeIsRoot: s.treeIsRootSelector(state),
    treeIsBrowsable: s.treeIsBrowsableSelector(state),
    treeTotalCount: s.treeTotalCountSelector(state),
    isMini: true,
    ...ownProps,
});
//#endregion

const PickerContainer = compose(
    LocalStoreHandler({ name: viewName }),
    connect(mapStateToProps),
    TogglesHandler({ viewName }),
    GlobalEventsHandler({
        items: [ServerEventItemType.folder],
        actions: [ServerEventActionType.delete],
        refreshMode: RefreshMode.Originator,
        refreshMessage: loc.t("globalEvents:folder.delete"),
        refreshRoute: true,
    }),
    SplitPanes({
        viewName,
        paneIds: ["#picker_treePanel", "#picker_docListPanel"],
        toggleIds: ["tree", "doclist"],
        direction: "horizontal",
    })
)(Picker);

export default PickerContainer;
